import clsx from 'clsx'
import { nothing, unsafeCSS } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { html } from 'lit/static-html.js'

import { AvatarShape, AvatarVariant, Size } from '@/dap-design-system'

import { DdsElement } from '../../internal/dds-hu-element'
import { SizedMixin } from '../../internal/mixin/sizedMixin'
import styles from './avatar.scss?inline'

const AvatarBaseWithSized = SizedMixin(DdsElement)

/**
 * `dap-ds-avatar`
 * @summary Avatar component can be used to display user profile images, initials, or icons.
 * @element dap-ds-avatar
 * @title - Avatar
 *
 * @slot icon - The icon to display when variant is 'icon'.
 * @slot fallback - Custom fallback content when image fails to load.
 *
 * @event dds-load - Fired when the image loads successfully.
 * @event dds-error - Fired when the image fails to load.
 *
 * @csspart base - The main avatar container.
 * @csspart img - The avatar image.
 * @csspart initials - The initials container.
 * @csspart icon - The icon container.
 * @csspart fallback - The fallback content container.
 * @csspart loading - The loading indicator.
 *
 * @cssproperty --dds-avatar-border-radius - The border radius of the avatar (default: 50%)
 * @cssproperty --dds-avatar-background-color - The background color of the avatar (default: var(--dds-neutral-200))
 * @cssproperty --dds-avatar-border-width - The border width of the avatar (default: 0)
 * @cssproperty --dds-avatar-border-color - The color of the avatar's border (default: transparent)
 * @cssproperty --dds-avatar-border-style - The style of the avatar's border (default: solid)
 * @cssproperty --dds-avatar-transition - The transition property for the avatar (default: all 0.2s ease-in-out)
 * @cssproperty --dds-avatar-text-color - The text color for initials (default: var(--dds-text-neutral-strong))
 * @cssproperty --dds-avatar-font-weight - The font weight for initials (default: var(--dds-font-weight-bold))
 * @cssproperty --dds-avatar-lg-size - Size for large avatars (default: var(--dds-avatar-size-lg))
 * @cssproperty --dds-avatar-md-size - Size for medium avatars (default: var(--dds-avatar-size-md))
 * @cssproperty --dds-avatar-sm-size - Size for small avatars (default: var(--dds-avatar-size-sm))
 * @cssproperty --dds-avatar-xs-size - Size for extra small avatars (default: var(--dds-avatar-size-xs))
 * @cssproperty --dds-avatar-xxs-size - Size for extra extra small avatars (default: var(--dds-avatar-size-xxs))
 * @cssproperty --dds-avatar-font-size-lg - Font size for large avatars (default: var(--dds-font-2xl))
 * @cssproperty --dds-avatar-font-size-md - Font size for medium avatars (default: var(--dds-font-lg))
 * @cssproperty --dds-avatar-font-size-sm - Font size for small avatars (default: var(--dds-font-base))
 * @cssproperty --dds-avatar-font-size-xs - Font size for extra small avatars (default: var(--dds-font-sm))
 * @cssproperty --dds-avatar-font-size-xxs - Font size for extra extra small avatars (default: var(--dds-font-xs))
 * @cssproperty --dds-avatar-hover-transform - Transform applied on hover for interactive avatars (default: scale(1.05))
 * @cssproperty --dds-avatar-active-transform - Transform applied when active for interactive avatars (default: scale(0.95))
 * @cssproperty --dds-avatar-focus-ring - Focus ring style for interactive avatars (default: 0 0 0 2px var(--dds-focus-outer-ring))
 * @cssproperty --dds-avatar-loading-background - Background color when loading (default: var(--dds-neutral-100))
 * @cssproperty --dds-avatar-error-background - Background color when image fails to load (default: var(--dds-negative-100))
 * @cssproperty --dds-avatar-error-color - Text color when image fails to load (default: var(--dds-negative-600))
 *
 * @property { 'xxs' | 'xs' | 'sm' | 'md' | 'lg' } size - The size of the avatar. Default is `md`.
 * @property {string} sizeMap - Responsive size map (e.g. "md:lg").
 */
@customElement('dap-ds-avatar')
export default class DapDSAvatar extends AvatarBaseWithSized {
  /** The shape of the avatar
   * @default circle
   * @type { 'circle' | 'rounded' | 'square' }
   * @example
   * ```html
   * <dap-ds-avatar shape="circle">
   * <dap-ds-avatar shape="rounded">
   * <dap-ds-avatar shape="square">
   * ```
   */
  @property({ reflect: true }) shape: AvatarShape = 'circle'
  /** The variant type of the avatar
   * @default image
   * @type { 'image' | 'initials' | 'icon' }
   * @example
   * ```html
   * <dap-ds-avatar variant="image">
   * <dap-ds-avatar variant="initials">
   * <dap-ds-avatar variant="icon">
   * ```
   */
  @property({ reflect: true }) variant: AvatarVariant = 'image'
  /** The source of the avatar image */
  @property() src: string
  /** The alt text of the avatar */
  @property() alt: string
  /** The initials to display when variant is 'initials' */
  @property() initials: string
  /** Accessible label for the avatar */
  @property() label: string
  /** Loading state indicator */
  @property({ type: Boolean }) loading = false
  /** Whether the avatar is interactive (clickable) */
  @property({ type: Boolean, reflect: true }) interactive = false
  /** The width of the avatar. This will override the size */
  @property({ type: Number }) width: number
  /** The height of the avatar. This will override the size */
  @property({ type: Number }) height: number

  @state() private hasImageError = false

  constructor() {
    super()
    this.size = 'md'
  }
  @state() private isImageLoading = true

  static readonly styles = unsafeCSS(styles)

  private handleImageLoad = () => {
    this.isImageLoading = false
    this.hasImageError = false
    this.emit('dds-load')
  }

  private handleImageError = () => {
    this.isImageLoading = false
    this.hasImageError = true
    this.emit('dds-error')
  }

  private handleClick = () => {
    if (this.interactive) {
      this.click()
    }
  }

  private generateInitials(initials?: string): string {
    if (!initials || !initials.trim()) return ''
    return initials
      .trim()
      .split(' ')
      .filter(word => word.length > 0)
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  private mapSizeToSpinnerSize(
    size: Size,
  ): 'xxl' | 'xl' | 'lg' | 'md' | 'sm' | 'xs' {
    switch (size) {
      case 'xxs':
        return 'xs'
      case 'xs':
        return 'sm'
      case 'sm':
        return 'md'
      case 'md':
        return 'lg'
      case 'lg':
        return 'xl'
      default:
        return 'lg'
    }
  }

  private renderImage() {
    if (!this.src || this.hasImageError) return nothing

    return html`
      <img
        class=${clsx('avatar__img', `avatar__img--${this.effectiveSize}`)}
        style=${this.width && this.height
          ? `width: ${this.width}px; height: ${this.height}px;`
          : ''}
        part="img"
        alt=${ifDefined(this.alt)}
        src=${this.src}
        loading="lazy"
        @load=${this.handleImageLoad}
        @error=${this.handleImageError} />
    `
  }

  private renderInitials() {
    const displayInitials = this.generateInitials(this.initials)
    if (!displayInitials) return null

    return html`
      <div
        class=${clsx(
          'avatar__initials',
          `avatar__initials--${this.effectiveSize}`,
        )}
        part="initials"
        aria-label=${ifDefined(this.label || `Avatar for ${this.initials}`)}>
        ${displayInitials}
      </div>
    `
  }

  private renderIcon() {
    return html`
      <div
        class=${clsx('avatar__icon', `avatar__icon--${this.effectiveSize}`)}
        part="icon"
        aria-label=${ifDefined(this.label || 'Avatar icon')}>
        <slot name="icon"></slot>
      </div>
    `
  }

  private renderFallback() {
    return html`
      <div
        class=${clsx(
          'avatar__fallback',
          `avatar__fallback--${this.effectiveSize}`,
        )}
        part="fallback"
        aria-label=${ifDefined(this.label || 'Avatar')}>
        <slot name="fallback">
          <dap-ds-icon-user-line></dap-ds-icon-user-line>
        </slot>
      </div>
    `
  }

  private renderLoading() {
    if (!this.loading || !this.isImageLoading) return nothing

    return html`
      <div
        class=${clsx(
          'avatar__loading',
          `avatar__loading--${this.effectiveSize}`,
        )}
        part="loading"
        aria-label=${ifDefined(this.label || 'Loading avatar')}>
        <dap-ds-spinner
          size=${this.mapSizeToSpinnerSize(this.effectiveSize)}
          variant="brand"></dap-ds-spinner>
      </div>
    `
  }

  private renderContent() {
    if (this.loading && this.isImageLoading && this.variant === 'image') {
      return this.renderLoading()
    }

    switch (this.variant) {
      case 'image':
        if (this.src && !this.hasImageError) {
          return this.renderImage()
        } else if (this.initials) {
          return this.renderInitials()
        } else {
          return this.renderFallback()
        }
      case 'initials':
        return this.renderInitials() || this.renderFallback()
      case 'icon':
        return this.renderIcon()
      default:
        return this.renderFallback()
    }
  }

  render() {
    const baseClasses = clsx(
      'avatar',
      `avatar--${this.effectiveSize}`,
      `avatar--${this.shape}`,
      `avatar--${this.variant}`,
      {
        'avatar--interactive': this.interactive,
        'avatar--loading': this.loading && this.isImageLoading,
        'avatar--error': this.hasImageError,
      },
    )

    return html`
      <div
        class=${baseClasses}
        part="base"
        role=${ifDefined(this.interactive ? 'button' : undefined)}
        tabindex=${ifDefined(this.interactive ? 0 : undefined)}
        @keydown=${(e: KeyboardEvent) => {
          if (this.interactive && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault()
            this.handleClick()
          }
        }}>
        ${this.renderContent()}
      </div>
    `
  }
}
